import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Table, Button, Badge, Alert, Modal, Form, Card, InputGroup, Pagination } from 'react-bootstrap';
import axios from 'axios';

interface ReferralRelation {
  _id: string;
  referrer: {
    _id: string;
    name: string;
    email: string;
    referralCode: string;
    userType: string;
  };
  referredUser: {
    _id: string;
    name: string;
    email: string;
    userType: string;
    createdAt: string;
  };
  referralCode: string;
  status: 'pending' | 'completed' | 'rewarded' | 'cancelled';
  referredAt: string;
  completedAt?: string;
  rewardedAt?: string;
  rewards: {
    referrerReward: number;
    refereeReward: number;
  };
}

interface ReferralStats {
  total: number;
  byStatus: {
    [key: string]: {
      count: number;
      referrerRewards: number;
      refereeRewards: number;
    };
  };
  totalRewards: number;
}

const AdminReferralsManagement: React.FC = () => {
  const [referrals, setReferrals] = useState<ReferralRelation[]>([]);
  const [stats, setStats] = useState<ReferralStats>({
    total: 0,
    byStatus: {},
    totalRewards: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'completed' | 'rewarded' | 'cancelled'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showChainModal, setShowChainModal] = useState(false);
  const [selectedUserChain, setSelectedUserChain] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  const loadReferrals = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString()
      });
      
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchTerm.trim()) params.append('search', searchTerm.trim());
      
      const response = await axios.get(`${API_BASE_URL}/admin/referrals?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setReferrals(response.data.referrals);
        setStats(response.data.stats);
        setTotalPages(response.data.pagination.total);
      }
    } catch (error: any) {
      console.error('Error loading referrals:', error);
      setError(error.response?.data?.message || 'Failed to load referrals');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm, currentPage, pageSize, API_BASE_URL]);

  useEffect(() => {
    loadReferrals();
  }, [loadReferrals]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    loadReferrals();
  };

  const viewReferralChain = async (userId: string, userName: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/admin/referrals/chain/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setSelectedUserChain({
          ...response.data,
          userName
        });
        setShowChainModal(true);
      }
    } catch (error: any) {
      console.error('Error loading referral chain:', error);
      setError(error.response?.data?.message || 'Failed to load referral chain');
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: { [key: string]: string } = {
      pending: 'warning',
      completed: 'info',
      rewarded: 'success',
      cancelled: 'danger'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status.toUpperCase()}</Badge>;
  };

  const getUserTypeBadge = (userType: string) => {
    const variants: { [key: string]: string } = {
      free: 'secondary',
      pro: 'warning',
      admin: 'danger'
    };
    const type = userType || 'free';
    return <Badge bg={variants[type] || 'secondary'} className="small">{type.toUpperCase()}</Badge>;
  };

  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <Row className="align-items-center">
          <Col>
            <h5 className="mb-0">
              <i className="fas fa-sitemap me-2"></i>
              Referral Management
            </h5>
          </Col>
          <Col xs="auto">
            <Form.Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as any);
                setCurrentPage(1);
              }}
              style={{ width: 'auto', display: 'inline-block' }}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="rewarded">Rewarded</option>
              <option value="cancelled">Cancelled</option>
            </Form.Select>
          </Col>
        </Row>
      </div>

      <div className="admin-card-body">
        {/* Search */}
        <Row className="mb-4">
          <Col md={8}>
            <Form onSubmit={handleSearch}>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Search by user name, email, or referral code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Button type="submit" variant="outline-primary">
                  <i className="fas fa-search"></i>
                </Button>
                {searchTerm && (
                  <Button 
                    variant="outline-secondary"
                    onClick={() => {
                      setSearchTerm('');
                      setCurrentPage(1);
                    }}
                  >
                    <i className="fas fa-times"></i>
                  </Button>
                )}
              </InputGroup>
            </Form>
          </Col>
        </Row>

        {/* Statistics Cards */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center">
                <div className="text-primary mb-2">
                  <i className="fas fa-sitemap fa-2x"></i>
                </div>
                <h4 className="mb-0">{stats.total}</h4>
                <small className="text-muted">Total Referrals</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center">
                <div className="text-warning mb-2">
                  <i className="fas fa-clock fa-2x"></i>
                </div>
                <h4 className="mb-0">{stats.byStatus.pending?.count || 0}</h4>
                <small className="text-muted">Pending</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center">
                <div className="text-success mb-2">
                  <i className="fas fa-check-circle fa-2x"></i>
                </div>
                <h4 className="mb-0">{stats.byStatus.rewarded?.count || 0}</h4>
                <small className="text-muted">Rewarded</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center">
                <div className="text-info mb-2">
                  <i className="fas fa-coins fa-2x"></i>
                </div>
                <h4 className="mb-0">₹{stats.totalRewards}</h4>
                <small className="text-muted">Total Rewards</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Alerts */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')}>
            <i className="fas fa-check-circle me-2"></i>
            {success}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <i className="fas fa-spinner fa-spin fa-2x mb-3"></i>
            <p>Loading referral data...</p>
          </div>
        ) : (
          <>
            {/* Referrals Table */}
            <div className="table-responsive">
              <Table hover>
                <thead>
                  <tr>
                    <th>Referrer</th>
                    <th>Referred User</th>
                    <th>Referral Code</th>
                    <th>Status</th>
                    <th>Referred Date</th>
                    <th>Rewards</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4">
                        <i className="fas fa-inbox fa-2x text-muted mb-3"></i>
                        <p className="text-muted mb-0">No referrals found</p>
                      </td>
                    </tr>
                  ) : (
                    referrals.map(referral => (
                      <tr key={referral._id}>
                        <td>
                          <div>
                            <strong>{referral.referrer.name}</strong>
                            <div className="small text-muted">{referral.referrer.email}</div>
                            <div className="mt-1">
                              {getUserTypeBadge(referral.referrer.userType)}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div>
                            <strong>{referral.referredUser.name}</strong>
                            <div className="small text-muted">{referral.referredUser.email}</div>
                            <div className="mt-1">
                              {getUserTypeBadge(referral.referredUser.userType)}
                            </div>
                          </div>
                        </td>
                        <td>
                          <code className="text-primary">{referral.referralCode}</code>
                        </td>
                        <td>
                          {getStatusBadge(referral.status)}
                        </td>
                        <td>
                          <small>{new Date(referral.referredAt).toLocaleDateString()}</small>
                          <div className="small text-muted">
                            {new Date(referral.referredAt).toLocaleTimeString()}
                          </div>
                        </td>
                        <td>
                          <div className="small">
                            <div className="text-success">
                              Referrer: ₹{referral.rewards.referrerReward}
                            </div>
                            <div className="text-info">
                              Referee: ₹{referral.rewards.refereeReward}
                            </div>
                          </div>
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => viewReferralChain(referral.referrer._id, referral.referrer.name)}
                            title="View Referral Chain"
                          >
                            <i className="fas fa-sitemap"></i>
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination Controls (standardized) */}
            <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
              <div>
                <Form.Select
                  style={{ width: 120, display: 'inline-block' }}
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  size="sm"
                >
                  {[5, 10, 20, 50, 100].map(size => (
                    <option key={size} value={size}>{size} / page</option>
                  ))}
                </Form.Select>
              </div>
              <div>
                <Pagination className="mb-0">
                  <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                  <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
                  {(() => {
                    let start = Math.max(1, currentPage - 2);
                    let end = Math.min(totalPages, currentPage + 2);
                    if (end - start < 4) {
                      if (start === 1) end = Math.min(totalPages, start + 4);
                      if (end === totalPages) start = Math.max(1, end - 4);
                    }
                    return Array.from({ length: end - start + 1 }, (_, i) => start + i).map(page => (
                      <Pagination.Item
                        key={page}
                        active={page === currentPage}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Pagination.Item>
                    ));
                  })()}
                  <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0} />
                  <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages || totalPages === 0} />
                </Pagination>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Referral Chain Modal */}
      <Modal show={showChainModal} onHide={() => setShowChainModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-sitemap me-2"></i>
            Referral Chain for {selectedUserChain?.userName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUserChain && (
            <div>
              {/* Who referred this user */}
              <div className="mb-4">
                <h6><i className="fas fa-arrow-up me-2"></i>Referred By:</h6>
                {selectedUserChain.referralChain.referredBy ? (
                  <Card className="border-primary">
                    <Card.Body>
                      <Row className="align-items-center">
                        <Col>
                          <strong>{selectedUserChain.referralChain.referredBy.referredBy.name}</strong>
                          <div className="small text-muted">
                            {selectedUserChain.referralChain.referredBy.referredBy.email}
                          </div>
                          <div className="mt-1">
                            Code: <code>{selectedUserChain.referralChain.referredBy.referralCode}</code>
                          </div>
                        </Col>
                        <Col xs="auto">
                          {getUserTypeBadge(selectedUserChain.referralChain.referredBy.referredBy.userType)}
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ) : (
                  <div className="text-muted">
                    <i className="fas fa-user me-2"></i>
                    This user was not referred by anyone (direct signup)
                  </div>
                )}
              </div>

              {/* User's referral statistics */}
              <div className="mb-4">
                <h6><i className="fas fa-chart-bar me-2"></i>Referral Statistics:</h6>
                <Row>
                  <Col md={3}>
                    <Card className="border-0 bg-light">
                      <Card.Body className="text-center">
                        <h5 className="mb-0 text-primary">{selectedUserChain.referralChain.stats.totalReferrals}</h5>
                        <small>Total Referrals</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="border-0 bg-light">
                      <Card.Body className="text-center">
                        <h5 className="mb-0 text-success">{selectedUserChain.referralChain.stats.rewardedReferrals}</h5>
                        <small>Rewarded</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="border-0 bg-light">
                      <Card.Body className="text-center">
                        <h5 className="mb-0 text-warning">₹{selectedUserChain.referralChain.stats.totalReferrerRewards}</h5>
                        <small>Total Earned</small>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </div>

              {/* Who this user has referred */}
              <div>
                <h6><i className="fas fa-arrow-down me-2"></i>Has Referred ({selectedUserChain.referralChain.hasReferred.length}):</h6>
                {selectedUserChain.referralChain.hasReferred.length > 0 ? (
                  <div className="table-responsive">
                    <Table size="sm" hover>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Type</th>
                          <th>Joined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedUserChain.referralChain.hasReferred.map((ref: any) => (
                          <tr key={ref._id}>
                            <td><strong>{ref.referredUser.name}</strong></td>
                            <td className="small text-muted">{ref.referredUser.email}</td>
                            <td>{getUserTypeBadge(ref.referredUser.userType)}</td>
                            <td className="small">{new Date(ref.referredUser.createdAt).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-muted">
                    <i className="fas fa-info-circle me-2"></i>
                    This user hasn't referred anyone yet
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowChainModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminReferralsManagement;